import React, { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Box } from "@mui/system";
import Menu from "./Menu";
import Header from "./Header";
import PermissionDeniedNotification from "./PermissionDeniedNotification";
import ChatWidget from "../chatBot/components/ChatWidget";
import SlidingServiceTab from "../../customer/serviceTracking/components/SlidingServiceTab";
import accountContext from "../../account/accountContext";
import NotificationToast from "../notifications/components/NotificationToast";
import { useFontTheme } from "../hooks/useFontTheme";
import { useAccentTheme, applyAccentTheme } from "../hooks/useAccentTheme";
import { ACCENT_THEME_STORAGE_KEY, DEFAULT_ACCENT_ID } from "../themes/accentThemes";
import customersApi from "../../customer/shared/api/customersApi";
import i18n from "../../core/i18n";
import usePermission from "../hooks/usePermission";
import { Modules } from "../../core/models/PermissionModels";

const Layout = () => {
  const { pathname } = useLocation();
  const isAdmin = pathname.includes("admin");
  const isAuthenticated = Boolean(accountContext.user);
  const { syncFromServer } = useFontTheme();
  const { syncFromServer: syncAccentFromServer } = useAccentTheme();
  const { hasAccess: hasServicingAccess } = usePermission(Modules.Servicing);

  // Apply accent theme from localStorage immediately on mount (before API call)
  useEffect(() => {
    try {
      const stored = localStorage.getItem(ACCENT_THEME_STORAGE_KEY);
      applyAccentTheme(stored || DEFAULT_ACCENT_ID);
    } catch {
      applyAccentTheme(DEFAULT_ACCENT_ID);
    }
  }, []);

  // Sync font + accent themes + language from Account service after login
  useEffect(() => {
    if (isAuthenticated) {
      customersApi.getAccount().then((account) => {
        if (account?.fontTheme) syncFromServer(account.fontTheme);
        if (account?.accentTheme) syncAccentFromServer(account.accentTheme);
        // Sync language preference: 1=en, 3=cs, 4=sk
        if (account?.languageId) {
          const languageIdToLocale: Record<number, string> = { 1: "en", 3: "cs", 4: "sk" };
          const locale = languageIdToLocale[account.languageId];
          if (locale && locale !== i18n.language) {
            i18n.changeLanguage(locale);
          }
        }
      }).catch(() => {
        // silent -- localStorage is the fallback for all preferences
      });
    }
  }, [isAuthenticated, syncFromServer, syncAccentFromServer]);

  return (
    <>
      {isAdmin && <Menu />}

      <Box className={`w-full ${isAdmin ? "v-panel" : "v-panel-no-sidebar"}`}>
        <Header />
        <Outlet />
      </Box>

      <PermissionDeniedNotification />
      <NotificationToast />
      {isAuthenticated && !isAdmin && hasServicingAccess && <SlidingServiceTab />}
      <ChatWidget />
    </>
  );
};

export default Layout;
